/**
 * FACTORY WORLD PAGE
 *
 * Main page component that orchestrates the Factory World UI.
 * Switches between World view, Kanban view, and Terminal view.
 */

import { useEffect, useState } from "react";
import { FolderOpen, Plus } from "lucide-react";
import { useWorldStore } from "@/stores/worldStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useUIStore } from "@/stores/uiStore";
import { Button } from "@/components/ui/button";
import { PixiWorld } from "@/features/factory/world/PixiWorld";
import { SelectionPanel } from "@/features/factory/world/SelectionPanel";
import { OperationsPanel } from "@/features/factory/world/OperationsPanel";
import { ControlsOverlay } from "@/features/factory/world/ControlsOverlay";
import { ProjectTooltip } from "@/features/factory/world/Tooltip";
import { ProjectCreationFlow } from "@/features/projects/ProjectCreationFlow";
import FactoryLoadingAnimation from "@/components/ui/FactoryLoadingAnimation";
import { useToast } from "@/hooks/use-toast";

export default function FactoryWorld() {
  const { isLoading, error, projects, fetchWorldData } = useWorldStore();
  const saveError = useWorldStore((s) => s.saveError);
  const clearSaveError = useWorldStore((s) => s.clearSaveError);
  const { ensureWorkspaceReady } = useWorkspaceStore();
  const { theme } = useUIStore();
  const { toast } = useToast();
  const [showAddProject, setShowAddProject] = useState(false);

  useEffect(() => {
    (async () => {
      await ensureWorkspaceReady();
      await fetchWorldData();
    })().catch((err) => console.error("[FactoryWorld] workspace init failed:", err));
  }, [ensureWorkspaceReady, fetchWorldData]);

  // Surface background save failures (drag/move/rename in the world) that would
  // otherwise fail silently, leaving the user thinking their change was saved.
  useEffect(() => {
    if (!saveError) return;
    toast({ title: "Change not saved", description: saveError, variant: "destructive" });
    clearSaveError();
  }, [saveError, clearSaveError, toast]);

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  if (isLoading) {
    return <FactoryLoadingAnimation />;
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center" data-testid="container-error">
        <div className="text-center">
          <p className="text-destructive font-medium mb-2">Failed to load world data</p>
          <p className="text-sm text-muted-foreground">{error}</p>
          <button
            onClick={() => fetchWorldData()}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
            <FolderOpen className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-lg font-semibold mb-2">Your colony is empty</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Add your first project to see it appear in the world view.
          </p>
          <Button onClick={() => setShowAddProject(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add your first project
          </Button>
        </div>
        {showAddProject && (
          <ProjectCreationFlow isOpen={showAddProject} onClose={() => setShowAddProject(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col overflow-hidden" data-testid="container-factory-world">
      <div className="flex-1 relative overflow-hidden">
        <PixiWorld />
        <ControlsOverlay />
        <ProjectTooltip />
        <SelectionPanel />
        <OperationsPanel />
      </div>
    </div>
  );
}
